import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { ReactElement, useRef, useState, cloneElement } from 'react';
import { chevron_down, chevron_up, favorites_icon, Icon, my_page } from '../Icon/Icon';
import './Dropdown.css';
import { Overlay } from '../../components/Overlay/Overlay';
import { useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { ApiUser } from '../../types';
import { useSession } from '../../state/session';
import { Button } from '../Form/Button';
import { NavLink } from 'react-router-dom';

type DropdownProps = {
	className?: string;
	label: string;
	items: ReactElement[];
	children: ReactElement | ReactElement[];
};

function MenuButton({ handleOpen, isOpen, label }: { handleOpen: () => void; isOpen: boolean; label: string }) {
	const t = useTranslation().navigation;
	const urls = useTranslation().routes;
	const { data: bonusData } = useApi(Res.session, {});
	const { isLoggedIn } = useSession();

	function customerText(apiData: boolean | ApiUser | undefined): string {
		let text = t.myPages;
		if (typeof apiData === 'object' && apiData.firstname.length !== 0 && apiData.firstname.length <= 16) {
			text = apiData.firstname;
		}
		return text;
	}

	function customerTextMobile(apiData: boolean | ApiUser | undefined): string {
		if (typeof apiData === 'object' && apiData.firstname.length !== 0) {
			return apiData.firstname.charAt(0).toUpperCase();
		}
		return '';
	}

	const customerDisplayName = customerTextMobile(bonusData);
	const noCustomerName = customerDisplayName.length === 0;

	return (
		<>
			<div
				className={`menu-button ${isOpen ? 'open' : ''} ${isLoggedIn ? 'logged-in' : ''}`}
				onClick={isOpen ? handleOpen : () => null}
			>
				<div className="not-mobile icon-wrapper" onClick={handleOpen}>
					<Icon size={24} className="my-page-desktop" shape={my_page} />
					<span>{label}</span>
					<Icon className="menu-button-chevron" size={25} shape={isOpen ? chevron_up : chevron_down} />
				</div>
				<div className="collapse-dropdown">
					<div
						className={`my-page-mobile-menu ${isOpen ? 'open' : ''} ${isLoggedIn ? 'logged-in' : ''} ${noCustomerName ? 'empty-name' : ''} `}
					>
						<div className="my-page-wrapper-mobile" onClick={handleOpen}>
							{noCustomerName && <Icon size={24} shape={my_page} className="my-page mobile-only" />}
							{!noCustomerName && <span className="customer-name-mobile">{customerDisplayName}</span>}
						</div>
						<div className="favorites-icon">
							{isLoggedIn && (
								<NavLink className="button clear navigation-item" to={urls.favorites}>
									<Button className="clear favorites">
										<Icon size={24} shape={favorites_icon} />
									</Button>
								</NavLink>
							)}
						</div>
					</div>
					{isOpen ? <Icon size={24} shape={my_page} className="my-page mobile-only" /> : null}
					<p className="customer-name">{customerText(bonusData)}</p>
					<Icon size={24} shape={chevron_up} className="chevron-up mobile-only" />
				</div>
			</div>
			<hr className={`menu-button-bottom-line ${isOpen ? 'open' : ''}`} />
		</>
	);
}

export function Dropdown({ label, items, children }: DropdownProps): JSX.Element {
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);

	const handleOpen = () => {
		setOpen(!open);
	};

	useOnClickOutside(dropdownRef, () => setOpen(false));

	return (
		<>
			{open ? <Overlay className="my-page-mobile-overlay" /> : null}
			<div ref={dropdownRef} className="dropdown">
				{open ? (
					<div className="dropdown-content">
						<MenuButton isOpen={true} label={label} handleOpen={handleOpen} />
						<ul className="menu">
							{items.map((item, index) => (
								<li key={index} className="menu-item">
									{cloneElement(item, {
										onClick: () => {
											if (item.props.onClick) {
												item.props.onClick();
											}
											setOpen(false);
										},
									})}
								</li>
							))}
						</ul>
						{children}
					</div>
				) : (
					<MenuButton isOpen={false} label={label} handleOpen={handleOpen} />
				)}
			</div>
		</>
	);
}
